import { useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../state/AuthContext";
import {
  CLIENT_ORG_TREE,
  SEED_CONTRACTS,
  clientPath,
  type DemoContract,
  type PricingType,
} from "../data/contractsSeed";
import { personById, unitById } from "../lib/orgHierarchy";

const PRICING: PricingType[] = [
  "FFP",
  "FFP_EPA",
  "T&M",
  "LH",
  "CPFF",
  "CPIF",
  "CPAF",
  "FPI",
  "IDIQ_ORDER",
  "OTHER",
];

function fmt(n: number) {
  return n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

export function ContractsPage() {
  const { moduleIds, homePath, roleIds } = useAuth();
  const can =
    moduleIds.includes("contracts") ||
    roleIds.includes("ROLE-CONTRACTS-SUBCONTRACTS") ||
    roleIds.includes("ROLE-PROJECT-MANAGER") ||
    roleIds.includes("ROLE-DIRECTOR") ||
    roleIds.includes("ROLE-VP");

  const isCon = roleIds.includes("ROLE-CONTRACTS-SUBCONTRACTS");
  const [contracts, setContracts] = useState<DemoContract[]>(() =>
    SEED_CONTRACTS.map((c) => ({ ...c, clients: c.clients.map((x) => ({ ...x })) })),
  );
  const [selectedId, setSelectedId] = useState(contracts[0]?.id ?? "");
  const [flash, setFlash] = useState("");

  const selected = useMemo(
    () => contracts.find((c) => c.id === selectedId) ?? null,
    [contracts, selectedId],
  );

  if (!can) return <Navigate to={homePath} replace />;

  function ping(msg: string) {
    setFlash(msg);
    window.setTimeout(() => setFlash(""), 3500);
  }

  function updateSelected(patch: Partial<DemoContract>) {
    if (!selected) return;
    setContracts((prev) =>
      prev.map((c) => (c.id === selected.id ? { ...c, ...patch } : c)),
    );
  }

  function setPrimaryClient(clientOrgId: string) {
    if (!selected || !isCon) return;
    const others = selected.clients.filter((l) => l.clientOrgId !== clientOrgId);
    updateSelected({
      clients: [{ clientOrgId, isPrimary: true }, ...others.map((o) => ({ ...o, isPrimary: false }))],
    });
    ping("Primary client updated.");
  }

  function addClient(clientOrgId: string) {
    if (!selected || !isCon) return;
    if (selected.clients.some((l) => l.clientOrgId === clientOrgId)) return;
    updateSelected({
      clients: [...selected.clients, { clientOrgId, isPrimary: selected.clients.length === 0 }],
    });
    ping("Additional client linked.");
  }

  function assignPm(pmId: string) {
    if (!selected || !isCon) return;
    const bu = unitById(selected.businessUnitId);
    const nextDir = selected.directorOverridden
      ? selected.directorId
      : bu?.assignedDirectorId || selected.directorId;
    const nextVp = selected.vpOverridden
      ? selected.vpId
      : bu?.assignedVpId || selected.vpId;
    updateSelected({
      pmId,
      directorId: nextDir || selected.directorId,
      vpId: nextVp || selected.vpId,
    });
    ping("PM assigned — Director/VP defaulted from BU leadership (non-overridden fields).");
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <header className="mb-6">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-brand-teal">
          REL-021 · DEC-PARTY-001 · DEC-CONTRACT-001/002
        </p>
        <h1 className="mt-2 font-display text-3xl text-navy">Contracts</h1>
        <p className="mt-2 max-w-3xl text-sm text-ink-muted leading-relaxed">
          Internal <code className="text-navy">VV.CCC</code> + period in charge codes; external
          customer number for search/SAM later. Primary + additional clients; CLIN pricing type;
          PM → Director / VP / BU attribution with override.
        </p>
        {flash && (
          <p className="mt-3 rounded-lg bg-brand-teal/10 px-3 py-2 text-sm text-navy">{flash}</p>
        )}
      </header>

      <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
        <ul className="rounded-xl border border-navy-100 bg-white divide-y divide-navy-50">
          {contracts.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => setSelectedId(c.id)}
                className={`w-full px-3 py-3 text-left text-sm ${
                  selectedId === c.id ? "bg-brand-teal/10" : "hover:bg-navy-50"
                }`}
              >
                <div className="font-semibold text-navy">{c.title}</div>
                <div className="font-mono text-xs text-ink-subtle">
                  {c.internalVvCcc} · PER {c.period}
                </div>
              </button>
            </li>
          ))}
        </ul>

        {selected && (
          <div className="space-y-4">
            <section className="rounded-xl border border-navy-100 bg-white p-5">
              <h2 className="font-display text-xl text-navy">{selected.title}</h2>
              <dl className="mt-3 grid gap-2 sm:grid-cols-2 text-sm">
                <div>
                  <dt className="text-xs uppercase text-ink-subtle">Internal</dt>
                  <dd className="font-mono text-navy">{selected.internalVvCcc}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase text-ink-subtle">Customer / gov #</dt>
                  <dd className="font-mono text-navy">{selected.customerContractNumber}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase text-ink-subtle">Period (PER)</dt>
                  <dd className="text-navy">{selected.period} (000=base)</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase text-ink-subtle">Funding triad</dt>
                  <dd className="text-navy">
                    A {fmt(selected.award)} · C {fmt(selected.ceiling)} · F {fmt(selected.funded)}
                  </dd>
                </div>
              </dl>
            </section>

            <section className="rounded-xl border border-navy-100 bg-white p-5">
              <h3 className="font-display text-lg text-navy">Clients</h3>
              <ul className="mt-2 space-y-2 text-sm">
                {selected.clients.map((l) => (
                  <li
                    key={l.clientOrgId}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-navy-50/60 px-3 py-2"
                  >
                    <span>
                      <span className="font-medium text-navy">{clientPath(l.clientOrgId)}</span>
                      {l.isPrimary && (
                        <span className="ml-2 text-xs font-semibold text-brand-teal">PRIMARY</span>
                      )}
                    </span>
                    {isCon && !l.isPrimary && (
                      <button
                        type="button"
                        className="text-xs font-semibold text-brand-teal"
                        onClick={() => setPrimaryClient(l.clientOrgId)}
                      >
                        Make primary
                      </button>
                    )}
                  </li>
                ))}
              </ul>
              {isCon && (
                <label className="mt-3 block text-xs">
                  Add client
                  <select
                    className="mt-1 w-full rounded-lg border border-navy-100 px-2 py-1.5 text-sm"
                    defaultValue=""
                    onChange={(e) => {
                      if (e.target.value) addClient(e.target.value);
                      e.target.value = "";
                    }}
                  >
                    <option value="">Select…</option>
                    {CLIENT_ORG_TREE.map((c) => (
                      <option key={c.id} value={c.id}>
                        {clientPath(c.id)}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </section>

            <section className="rounded-xl border border-navy-100 bg-white p-5">
              <h3 className="font-display text-lg text-navy">CLIN pricing type</h3>
              <div className="mt-2 overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="text-xs uppercase text-ink-subtle">
                    <tr>
                      <th className="py-1">CLIN</th>
                      <th className="py-1">Pricing type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selected.clinPricing.map((row, i) => (
                      <tr key={row.clin} className="border-t border-navy-50">
                        <td className="py-2 font-mono">{row.clin}</td>
                        <td className="py-2">
                          {isCon ? (
                            <select
                              className="rounded border border-navy-100 px-2 py-1"
                              value={row.pricingType}
                              onChange={(e) => {
                                const clinPricing = selected.clinPricing.map((x, j) =>
                                  j === i
                                    ? { ...x, pricingType: e.target.value as PricingType }
                                    : x,
                                );
                                updateSelected({ clinPricing });
                              }}
                            >
                              {PRICING.map((p) => (
                                <option key={p} value={p}>
                                  {p}
                                </option>
                              ))}
                            </select>
                          ) : (
                            row.pricingType
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-2 text-xs text-ink-subtle">
                Default for new CLINs: {selected.defaultPricingType}
              </p>
            </section>

            <section className="rounded-xl border border-navy-100 bg-white p-5">
              <h3 className="font-display text-lg text-navy">Delivery attribution</h3>
              <dl className="mt-3 grid gap-3 sm:grid-cols-2 text-sm">
                <div>
                  <dt className="text-xs uppercase text-ink-subtle">Project Manager</dt>
                  <dd>
                    {isCon ? (
                      <select
                        className="mt-0.5 w-full rounded border border-navy-100 px-2 py-1"
                        value={selected.pmId}
                        onChange={(e) => assignPm(e.target.value)}
                      >
                        <option value="emp-ortiz">Jordan Ortiz</option>
                      </select>
                    ) : (
                      personById(selected.pmId)?.name
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs uppercase text-ink-subtle">Business Unit</dt>
                  <dd className="text-navy">
                    {unitById(selected.businessUnitId)?.name ?? selected.businessUnitId}
                    {selected.buOverridden && (
                      <span className="ml-1 text-xs text-amber-700">(overridden)</span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs uppercase text-ink-subtle">Director</dt>
                  <dd className="text-navy">
                    {personById(selected.directorId)?.name}
                    {selected.directorOverridden && (
                      <span className="ml-1 text-xs text-amber-700">(overridden)</span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs uppercase text-ink-subtle">VP</dt>
                  <dd className="text-navy">
                    {personById(selected.vpId)?.name}
                    {selected.vpOverridden && (
                      <span className="ml-1 text-xs text-amber-700">(overridden)</span>
                    )}
                  </dd>
                </div>
              </dl>
              {selected.overrideReason && (
                <p className="mt-3 text-xs text-ink-muted">
                  Override reason: {selected.overrideReason}
                </p>
              )}
              {isCon && (
                <button
                  type="button"
                  className="mt-3 text-xs font-semibold text-brand-teal"
                  onClick={() => {
                    updateSelected({
                      directorId: "emp-dir-dod",
                      businessUnitId: "ou-bu-dod",
                      directorOverridden: true,
                      buOverridden: true,
                      overrideReason: "Manual credit adjustment (demo)",
                    });
                    ping("Director/BU overridden with audit reason.");
                  }}
                >
                  Demo: override Director / BU credit
                </button>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
